/**
 * Choosing where the camera goes so the model is actually visible.
 *
 * A user opened a solved reconstruction and the cloud occupied about two per
 * cent of a 1027×514 canvas. The cause is not the camera code - it is what the
 * camera was measuring. A sparse solve emits stray points: a handful of badly
 * constrained triangulations land hundreds of units from the subject, and a
 * plain min/max bounding box is then almost entirely empty space. Framing on
 * that box puts the subject in the middle two per cent of it, exactly as
 * observed.
 *
 * So framing uses a **robust** extent - the middle of the distribution per
 * axis - while the near and far planes keep using the **full** extent. Nothing
 * is hidden or dropped: every point is still uploaded and still drawn. The only
 * thing the outliers stop deciding is where the camera stands.
 *
 * The distance itself comes from the actual field of view and aspect ratio
 * rather than a constant multiple of the radius, so a tall narrow viewport
 * frames the same model as well as a wide one - and a phone frames it as well
 * as a desktop.
 */

import type { Vec3 } from "./viewerCamera";

/** The camera's own field of view, shared with `mvpMatrix`. */
export const FOV_DEGREES = 50;

/**
 * How much of the frame the model should leave as breathing room.
 *
 * 1.15 puts the bounding sphere at about 87% of the smaller dimension: filled
 * enough to read as "here is your model", loose enough that a rotation does
 * not immediately push a corner off screen.
 */
export const FIT_MARGIN = 1.15;

/**
 * How far into the tails to ignore when framing.
 *
 * 1.5% each end. Chosen to be smaller than any real feature of a capture and
 * larger than the handful of stray triangulations a solve produces; a cloud
 * with no outliers is unaffected, because its 1.5th percentile and its minimum
 * are the same place.
 */
const TAIL = 0.015;

/** Points sampled to estimate the percentiles. Deterministic, never random. */
const SAMPLE_LIMIT = 60_000;

export interface Framing {
  /** What the camera looks at: the middle of the bulk of the geometry. */
  center: Vec3;
  /** Bounding-sphere radius of that bulk. What the fit distance is derived from. */
  radius: number;
  /** Bounding-sphere radius of *everything*, for near and far planes. */
  sceneRadius: number;
}

function quantile(sorted: number[], fraction: number): number {
  if (sorted.length === 0) return 0;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.round(fraction * (sorted.length - 1))));
  return sorted[index] ?? 0;
}

/**
 * Where to point the camera, and how big the thing in front of it is.
 *
 * `positions` is the flat xyz buffer both viewers already hold, so this costs
 * one strided pass and three sorts of at most `SAMPLE_LIMIT` numbers.
 */
export function framingOf(positions: Float32Array): Framing {
  const count = Math.floor(positions.length / 3);
  if (count === 0) return { center: [0, 0, 0], radius: 1, sceneRadius: 1 };

  // A fixed stride rather than a random sample: the same file must frame the
  // same way every time it is opened.
  const stride = Math.max(1, Math.ceil(count / SAMPLE_LIMIT));
  const xs: number[] = [];
  const ys: number[] = [];
  const zs: number[] = [];
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];

  for (let point = 0; point < count; point += 1) {
    const i = point * 3;
    // `Number(...)` rather than an assertion: a solve can emit a non-finite
    // coordinate for a badly constrained point, and it is skipped rather than
    // poisoning every bound with NaN.
    const x = Number(positions[i]);
    const y = Number(positions[i + 1]);
    const z = Number(positions[i + 2]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    if (x < min[0]) min[0] = x;
    if (y < min[1]) min[1] = y;
    if (z < min[2]) min[2] = z;
    if (x > max[0]) max[0] = x;
    if (y > max[1]) max[1] = y;
    if (z > max[2]) max[2] = z;
    if (point % stride === 0) {
      xs.push(x);
      ys.push(y);
      zs.push(z);
    }
  }

  if (!Number.isFinite(min[0])) return { center: [0, 0, 0], radius: 1, sceneRadius: 1 };

  const sceneRadius =
    Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]) / 2 || 1;

  xs.sort((a, b) => a - b);
  ys.sort((a, b) => a - b);
  zs.sort((a, b) => a - b);

  const low: Vec3 = [quantile(xs, TAIL), quantile(ys, TAIL), quantile(zs, TAIL)];
  const high: Vec3 = [
    quantile(xs, 1 - TAIL),
    quantile(ys, 1 - TAIL),
    quantile(zs, 1 - TAIL),
  ];

  const center: Vec3 = [
    (low[0] + high[0]) / 2,
    (low[1] + high[1]) / 2,
    (low[2] + high[2]) / 2,
  ];
  // The half-diagonal, not half the largest edge: a box whose diagonal fits
  // the frustum fits it whichever way it is turned.
  const radius = Math.hypot(high[0] - low[0], high[1] - low[1], high[2] - low[2]) / 2 || 1;

  return { center, radius, sceneRadius: Math.max(sceneRadius, radius) };
}

/** The same, from a mesh's own vertex buffer. */
export const framingOfMesh = (positions: Float32Array): Framing => framingOf(positions);

/**
 * How far back the camera has to stand for a sphere of `radius` to fit.
 *
 * Derived from the frustum rather than from a constant: the vertical half
 * angle is half the field of view, the horizontal one widens with the aspect
 * ratio, and the binding constraint is whichever is smaller. A portrait phone
 * is therefore framed by its width and a wide monitor by its height, which is
 * what makes the same model look right on both.
 */
export function fitDistance(radius: number, aspect: number): number {
  const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  const vertical = (FOV_DEGREES * Math.PI) / 360;
  const horizontal = Math.atan(Math.tan(vertical) * safeAspect);
  const limiting = Math.min(vertical, horizontal);
  return (radius / Math.sin(limiting)) * FIT_MARGIN;
}
